import { type DroneResponse, DroneResponseOperationalStatus } from 'generatedTypes/asset/models';
import { hectaresToAcres } from 'utils/areaUtil';

import type { UsageSummary } from 'components/DroneCard/components/DroneCardDetails/types';
import type { Readiness } from 'components/DroneCard/types';

const SECONDS_PER_HOUR = 3600;

// Exhaustive over the API status enum: if the API adds a status (e.g. a real HOLD),
// this map fails to compile until it is handled. FALLBACK_READINESS is a runtime
// fail-safe for a status outside the generated enum (API drift) — never crash the card,
// and never treat an unknown drone as airworthy.
// The card's `Readiness` type also carries 'HOLD', reserved for when the API emits it.
const STATUS_TO_READINESS: Record<DroneResponseOperationalStatus, Readiness> = {
    [DroneResponseOperationalStatus.ACTIVE]: 'AIRWORTHY',
    [DroneResponseOperationalStatus.GROUNDED]: 'GROUNDED'
};

const FALLBACK_READINESS: Readiness = 'GROUNDED';

type DroneCardProps = {
    faaRegistrationNumber?: string;
    firmware: string;
    lastSyncedAt: Date;
    readiness: Readiness;
    serial?: string;
    usage: UsageSummary;
    warrantyEnd?: Date;
};

const getDroneCardProps = (drone: DroneResponse): DroneCardProps => ({
    faaRegistrationNumber: drone.faaRegistrationNumber,
    firmware: drone.flightControllerFirmwareVersion,
    lastSyncedAt: drone.updatedAt,
    readiness: STATUS_TO_READINESS[drone.operationalStatus] ?? FALLBACK_READINESS,
    serial: drone.flightControllerSerialNumber,
    usage: {
        acres: hectaresToAcres(drone.flownHectares),
        flightHours: Math.round(drone.flightSeconds / SECONDS_PER_HOUR),
        flights: drone.numberOfFlights
    },
    warrantyEnd: drone.warranty?.end
});

export default getDroneCardProps;
